import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { loadDREData, buildDRETree } from '../../../core/utils/financialDashboard';
import { loadAccountPlans } from '../../../core/utils/accountPlan';
import { exportDREToPDF } from '../../../core/utils/dreExport';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { DRE_PERIOD_OPTIONS, DRE_SECTIONS } from '../../../core/constants/financial';
import { MONTHS_SHORT, formatLocalISO } from '../../../core/utils/financialPeriod';

const getMonthRanges = (periodKey, customRange) => {
  const ranges = [];

  if (periodKey === 'custom') {
    if (!customRange?.from || !customRange?.to) return ranges;
    const [fy, fm] = customRange.from.split('-').map(Number);
    const [ty, tm] = customRange.to.split('-').map(Number);
    const startIdx = fy * 12 + (fm - 1);
    const endIdx   = ty * 12 + (tm - 1);
    if (endIdx < startIdx) return ranges;
    for (let i = startIdx; i <= endIdx; i++) {
      const y = Math.floor(i / 12);
      const m = i % 12;
      const start = new Date(y, m, 1);
      const end   = new Date(y, m + 1, 0);
      ranges.push({
        label: `${MONTHS_SHORT[m]}/${String(y).slice(2)}`,
        from: formatLocalISO(start),
        to:   formatLocalISO(end),
      });
    }
    return ranges;
  }

  const opt = DRE_PERIOD_OPTIONS.find((o) => o.key === periodKey);
  if (!opt || !opt.months) return ranges;
  const months = opt.months;
  const offset = opt.offset || 0;
  const now = new Date();
  for (let i = months - 1 + offset; i >= offset; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    ranges.push({
      label: `${MONTHS_SHORT[d.getMonth()]}/${d.getFullYear().toString().slice(2)}`,
      from: formatLocalISO(d),
      to: formatLocalISO(end),
    });
  }
  return ranges;
};

const getTotalRange = (monthRanges) => {
  if (monthRanges.length === 0) return { from: '', to: '' };
  return { from: monthRanges[0].from, to: monthRanges[monthRanges.length - 1].to };
};

const sumGroups = (groups) => groups.reduce((s, g) => s + (g.total || 0), 0);

const computeTotalsFromTree = (tree) => {
  const receita = sumGroups(tree.receita);
  const deducao = sumGroups(tree.deducao);
  const custo = sumGroups(tree.custo);
  const despesa = sumGroups(tree.despesa);
  const receitaLiquida = receita - deducao;
  const lucroBruto = receitaLiquida - custo;
  const resultado = lucroBruto - despesa;
  return {
    receita, deducao, custo, despesa,
    _receita_liquida: receitaLiquida,
    _lucro_bruto: lucroBruto,
    _resultado: resultado,
  };
};

const groupByMonth = (entries, monthRanges, allAccounts) => {
  return monthRanges.map((mr) => {
    const monthEntries = entries.filter((e) => e.issue_date >= mr.from && e.issue_date <= mr.to);
    const tree = buildDRETree(monthEntries, allAccounts);
    return { label: mr.label, from: mr.from, to: mr.to, tree, totals: computeTotalsFromTree(tree) };
  });
};

const fmtPct = (value, base) => {
  if (!base) return '—';
  return `${(value / base * 100).toFixed(1)}%`;
};

const findGroupInMonthTree = (monthTree, sectionKey, groupId) => {
  const stack = [...(monthTree[sectionKey] || [])];
  while (stack.length) {
    const node = stack.shift();
    if (node.groupId === groupId) return node;
    if (node.children) stack.push(...node.children);
  }
  return null;
};

// Linha de lançamento da visão analítica: discrimina cada lançamento que
// compõe o valor da conta. Com meses discriminados, o valor sai na coluna
// do mês da emissão.
const DREEntryRow = ({ entry, depth, showMonthCols, monthData }) => {
  const value = Number(entry.total_value) || 0;
  const party = entry.supplier_client || entry.description || '—';
  return (
    <tr className="fd-dre-row-entry">
      <td>
        <span className="fd-dre-item-tree" style={{ paddingLeft: depth * 14 }}>·</span>
        {formatDateBR(entry.issue_date)} · {party}
        {entry.document ? ` · ${entry.document}` : ''}
      </td>
      <td style={{ textAlign: 'right', fontFamily: "'Rajdhani', sans-serif" }}>
        {formatCurrency(value)}
      </td>
      <td />
      {showMonthCols && monthData.map((md) => {
        const inMonth = entry.issue_date >= md.from && entry.issue_date <= md.to;
        return (
          <td key={md.label} className="fd-dre-month-col">
            {inMonth ? formatCurrency(value) : '—'}
          </td>
        );
      })}
    </tr>
  );
};

const DRETreeRow = ({ node, depth, sectionKey, showMonthCols, monthData, analytic }) => {
  const hasChildren = node.children && node.children.length > 0;
  return (
    <>
      <tr className="fd-dre-row-item">
        <td>
          <span className="fd-dre-item-tree" style={{ paddingLeft: depth * 14 }}>{depth === 1 ? '└' : '·'}</span>
          {node.name}
        </td>
        <td style={{ textAlign: 'right', fontFamily: "'Rajdhani', sans-serif" }}>
          {formatCurrency(node.total)}
        </td>
        <td />
        {showMonthCols && monthData.map((md) => {
          const monthNode = findGroupInMonthTree(md.tree, sectionKey, node.groupId);
          return (
            <td key={md.label} className="fd-dre-month-col">
              {monthNode ? formatCurrency(monthNode.total) : '—'}
            </td>
          );
        })}
      </tr>
      {analytic && node.entries.map((entry) => (
        <DREEntryRow
          key={entry.id}
          entry={entry}
          depth={depth + 1}
          showMonthCols={showMonthCols}
          monthData={monthData}
        />
      ))}
      {hasChildren && node.children.map((child) => (
        <DRETreeRow
          key={child.groupId}
          node={child}
          depth={depth + 1}
          sectionKey={sectionKey}
          showMonthCols={showMonthCols}
          monthData={monthData}
          analytic={analytic}
        />
      ))}
    </>
  );
};

const DREModal = ({ isOpen, onClose }) => {
  const company = useSelector((state) => state.app.company || {});
  const [selectedPeriod, setSelectedPeriod] = useState('mes');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [entries, setEntries] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [mode, setMode] = useState('resumido');
  const [showMonths, setShowMonths] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPeriod('mes');
    setCustomRange({ from: '', to: '' });
  }, [isOpen]);

  const monthRanges = useMemo(
    () => getMonthRanges(selectedPeriod, customRange),
    [selectedPeriod, customRange.from, customRange.to]
  );
  const totalRange = useMemo(() => getTotalRange(monthRanges), [monthRanges]);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    loadAccountPlans(company.id).then((data) => {
      if (cancelled) return;
      setAllAccounts(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [isOpen, company.id]);

  useEffect(() => {
    if (!isOpen || !company.id || !totalRange.from) return;
    let cancelled = false;
    setLoading(true);
    loadDREData(company.id, totalRange).then((data) => {
      if (cancelled) return;
      setEntries(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, company.id, totalRange.from, totalRange.to]);

  const tree = useMemo(() => buildDRETree(entries, allAccounts), [entries, allAccounts]);
  const totals = useMemo(() => computeTotalsFromTree(tree), [tree]);
  const monthData = useMemo(
    () => (showMonths && monthRanges.length > 1) ? groupByMonth(entries, monthRanges, allAccounts) : [],
    [entries, monthRanges, showMonths, allAccounts]
  );

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setModeHandler = (m) => {
    setMode(m);
    if (m === 'resumido') {
      setExpanded({});
    } else {
      const allOpen = {};
      DRE_SECTIONS.filter((s) => s.type === 'group').forEach((s) => { allOpen[s.key] = true; });
      setExpanded(allOpen);
    }
  };

  const analytic = mode === 'analitico';

  const totalDespesas = totals.despesa;
  const lucratividade = totals.receita > 0 ? (totals._resultado / totals.receita * 100).toFixed(1) : '0.0';
  const margemBruta = totals.receita > 0 ? (totals._lucro_bruto / totals.receita * 100).toFixed(1) : '0.0';

  const periodLabel = monthRanges.length === 0
    ? '—'
    : monthRanges.length === 1
      ? monthRanges[0].label
      : `${monthRanges[0]?.label} – ${monthRanges[monthRanges.length - 1]?.label} (${monthRanges.length} meses)`;

  const showMonthCols = showMonths && monthData.length > 1;

  if (!isOpen) return null;

  return (
    <div className="fd-modal-overlay fd-modal-overlay--top" onClick={onClose}>
      <div className="fd-modal fd-modal--xl" onClick={(e) => e.stopPropagation()}>
        <div className="fd-modal-header">
          <div className="fd-modal-header-left">
            <h3 className="fd-modal-title" style={{ letterSpacing: 2 }}>DRE</h3>
            <span className="fd-table-muted" style={{ fontSize: 10 }}>Demonstração do Resultado do Exercício</span>
            <span className="fd-dre-badge">{periodLabel}</span>
            <span
              className="fd-dre-badge fd-dre-badge--info"
              title="Soma o que foi emitido/faturado no período (data de emissão), independente de já ter sido pago ou recebido. É o padrão contábil oficial da DRE."
            >
              Por competência
            </span>
          </div>
          <div className="fd-modal-header-right">
            <button
              type="button"
              className="fd-btn-secondary"
              disabled={exporting || loading}
              onClick={async () => {
                setExporting(true);
                try {
                  await exportDREToPDF({
                    elementId: 'dre-export-area',
                    periodLabel,
                    companyName: company.name || 'Empresa',
                  });
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? 'Exportando…' : 'Exportar PDF'}
            </button>
            <button className="fd-btn-close" type="button" onClick={onClose}>Fechar</button>
          </div>
        </div>

        <div className="fd-dre-filters">
          <span className="fd-period-filters-label">Período:</span>
          <div className="fd-dre-period-btns">
            {DRE_PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`fd-period-btn ${selectedPeriod === opt.key ? 'fd-period-btn--active' : ''}`}
                onClick={() => setSelectedPeriod(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {selectedPeriod === 'custom' && (
            <div className="fd-dre-custom-range">
              <input
                type="month"
                className="fd-dre-month-input"
                value={customRange.from}
                onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
              />
              <span className="fd-dre-custom-range-sep">até</span>
              <input
                type="month"
                className="fd-dre-month-input"
                value={customRange.to}
                onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          )}

          <div className="fd-dre-controls">
            <span className="fd-period-filters-label">Visualização:</span>
            <div className="fd-dre-mode-toggle">
              <button
                type="button"
                className={`fd-dre-mode-btn ${mode === 'resumido' ? 'fd-dre-mode-btn--active' : ''}`}
                onClick={() => setModeHandler('resumido')}
              >
                Resumido
              </button>
              <button
                type="button"
                className={`fd-dre-mode-btn ${mode === 'expandido' ? 'fd-dre-mode-btn--active' : ''}`}
                onClick={() => setModeHandler('expandido')}
              >
                Expandido
              </button>
              <button
                type="button"
                className={`fd-dre-mode-btn ${mode === 'analitico' ? 'fd-dre-mode-btn--active' : ''}`}
                onClick={() => setModeHandler('analitico')}
              >
                Analítico
              </button>
            </div>

            {monthRanges.length > 1 && (
              <>
                <span className="fd-period-filters-label">Discriminar meses</span>
                <div
                  className={`fd-dre-toggle ${showMonths ? 'fd-dre-toggle--on' : ''}`}
                  onClick={() => setShowMonths(!showMonths)}
                >
                  <div className="fd-dre-toggle-ball" />
                </div>
              </>
            )}
          </div>
        </div>

        <div id="dre-export-area">
          <div className="fd-dre-kpis">
            <div className="fd-dre-kpi">
              <span className="fd-dre-kpi-label" style={{ color: '#70E000' }}>Receita Bruta</span>
              <span className="fd-dre-kpi-value" style={{ color: '#70E000' }}>{formatCurrency(totals.receita)}</span>
            </div>
            <div className="fd-dre-kpi">
              <span className="fd-dre-kpi-label" style={{ color: '#60b4f6' }}>Lucro Bruto</span>
              <span className="fd-dre-kpi-value" style={{ color: '#60b4f6' }}>{formatCurrency(totals._lucro_bruto)}</span>
              <span className="fd-dre-kpi-sub">Margem {margemBruta}%</span>
            </div>
            <div className="fd-dre-kpi">
              <span className="fd-dre-kpi-label" style={{ color: totals._resultado >= 0 ? '#70E000' : '#ED2D2D' }}>Lucro Líquido</span>
              <span className="fd-dre-kpi-value" style={{ color: totals._resultado >= 0 ? '#70E000' : '#ED2D2D' }}>{formatCurrency(totals._resultado)}</span>
              <span className="fd-dre-kpi-sub">Lucratividade {lucratividade}%</span>
            </div>
            <div className="fd-dre-kpi">
              <span className="fd-dre-kpi-label" style={{ color: '#e88c2a' }}>Total Despesas</span>
              <span className="fd-dre-kpi-value" style={{ color: '#e88c2a' }}>{formatCurrency(totalDespesas)}</span>
            </div>
            <div className="fd-dre-kpi">
              <span className="fd-dre-kpi-label">Meses Analisados</span>
              <span className="fd-dre-kpi-value">{monthRanges.length}</span>
              <span className="fd-dre-kpi-sub">{monthRanges.length > 1 ? 'Acumulado' : 'Mensal'}</span>
            </div>
          </div>

          <div className="fd-modal-table-wrapper">
            {loading ? (
              <div className="fd-table-empty" style={{ padding: 40 }}>Carregando...</div>
            ) : (
              <table className="fd-modal-table fd-dre-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 280 }}>CONTA</th>
                    <th style={{ width: 140, textAlign: 'right' }}>
                      {monthRanges.length > 1 ? 'TOTAL PERÍODO' : monthRanges[0]?.label?.toUpperCase()}
                    </th>
                    <th style={{ width: 80, textAlign: 'right' }}>% REC.</th>
                    {showMonthCols && monthData.map((md) => (
                      <th key={md.label} className="fd-dre-month-col">{md.label.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DRE_SECTIONS.map((section) => {
                    if (section.type === 'subtotal' || section.type === 'result') {
                      const value = totals[section.key];
                      const isResult = section.type === 'result';
                      const color = isResult ? (value >= 0 ? '#70E000' : '#ED2D2D') : '#60b4f6';
                      const rowClass = isResult ? 'fd-dre-row-result' : 'fd-dre-row-subtotal';
                      return (
                        <React.Fragment key={section.key}>
                          <tr className={rowClass}>
                            <td style={{ fontWeight: 700 }}>{section.label}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color, fontFamily: "'Rajdhani', sans-serif", fontSize: isResult ? 15 : 13 }}>
                              {formatCurrency(value)}
                            </td>
                            <td style={{ textAlign: 'right', fontSize: 10, color: '#999' }}>
                              {fmtPct(value, totals.receita)}
                            </td>
                            {showMonthCols && monthData.map((md) => {
                              const mv = md.totals[section.key];
                              return (
                                <td key={md.label} className="fd-dre-month-col" style={{ color: isResult ? (mv >= 0 ? '#70E000' : '#ED2D2D') : '#999' }}>
                                  {formatCurrency(mv)}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="fd-dre-spacer"><td colSpan={99} /></tr>
                        </React.Fragment>
                      );
                    }

                    const groups = tree[section.key] || [];
                    const sectionTotal = totals[section.key];
                    const isExpanded = expanded[section.key];

                    // Quando há apenas 1 grupo nível 1 com filhos, pula o grupo
                    // (que teria total idêntico ao bloco) e mostra os filhos
                    // diretamente sob o bloco fixo. Evita redundância visual em
                    // planos de contas rasos. Com múltiplos grupos nível 1,
                    // mantém cada um como linha clicável. No analítico, não pula
                    // se o grupo tiver lançamentos diretos — sumiriam junto.
                    const skipSingleRoot =
                      groups.length === 1 &&
                      groups[0].children?.length > 0 &&
                      !(analytic && groups[0].entries?.length > 0);
                    const rootsToRender = skipSingleRoot ? groups[0].children : groups;

                    return (
                      <React.Fragment key={section.key}>
                        <tr
                          className="fd-dre-row-group"
                          onClick={() => rootsToRender.length > 0 && toggleExpand(section.key)}
                          style={{ cursor: rootsToRender.length > 0 ? 'pointer' : 'default' }}
                        >
                          <td>
                            {rootsToRender.length > 0 && (
                              <span className="fd-dre-expand">{isExpanded ? '▼' : '▶'}</span>
                            )}
                            {section.label}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>
                            {formatCurrency(sectionTotal)}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 10, color: '#999' }}>
                            {fmtPct(sectionTotal, totals.receita)}
                          </td>
                          {showMonthCols && monthData.map((md) => (
                            <td key={md.label} className="fd-dre-month-col" style={{ color: '#999' }}>
                              {formatCurrency(md.totals[section.key])}
                            </td>
                          ))}
                        </tr>
                        {isExpanded && rootsToRender.map((group) => (
                          <DRETreeRow
                            key={group.groupId}
                            node={group}
                            depth={1}
                            sectionKey={section.key}
                            showMonthCols={showMonthCols}
                            monthData={monthData}
                            analytic={analytic}
                          />
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DREModal;
